import { Inject, Injectable, NgZone } from '@angular/core';
import { LoadingScreenService } from './loading-screen.service';
import { AccessLevels } from '../models/access-levels';
import { BehaviorSubject, Observable, of } from 'rxjs';
import * as _ from 'underscore';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { ModalService } from './modal.service';
import { ModalStates } from '../models/modal';
import { AuditService } from './audit.service';
import { User } from '../models/user';
import { OAuthService } from 'angular-oauth2-oidc';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { SignOutToolsComponent } from '../tools/sign-out-tools.component';
import { finalize } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { Store } from '@ngxs/store';
import { ClearClientState } from '../state/ClientSelectionState/ClientSelection.state';
import {
  AuthenticationLogin,
  AuthenticationState,
  ClearAuthState
} from '../state/AuthenticationState/Authentication.state';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public isLoginComplete$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private _user: User;
  public userLoginStatus: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private auditService: AuditService,
    private http: HttpClient,
    private loadingScreenService: LoadingScreenService,
    private modalService: ModalService,
    private ngModalService: NgbModal,
    private ngZone: NgZone,
    private oauthService: OAuthService,
    private router: Router,
    private signOutTools: SignOutToolsComponent,
    private store: Store,
    @Inject('env') private env: any,
    @Inject('authCodeFlowConfig') private authCodeFlowConfig: any,
    @Inject('loadingInterceptorSkipHeader') private loadingInterceptorSkipHeader: any,
    @Inject('errorInterceptorSkipHeader') private errorInterceptorSkipHeader: any,
    @Inject('loginRedirectSkipHeader') private loginRedirectSkipHeader: any
  ) {
    this.loadingScreenService.startLoading();
  }

  get user() {
    this._setUser();
    return this._user;
  }

  public getUser() {
    return this.user;
  }

  public canRead(permission: string): boolean {
    return this.hasPermission(permission, AccessLevels.Read);
  }

  public checkBrowserCompatibility() {
    // detect IE, and pre-chromium Edge
    if (/msie\s|trident\/|edge\//i.test(window.navigator.userAgent)) {
      const msg =
        'Sorry, it appears you are using a browser that is not currently supported. Please use the latest version one of the following browsers: Edge (v80+), Chrome, Firefox, Safari.';
      this.modalService.showAlertModal(msg, ModalStates.Error);
    }
  }

  public completeAuthentication(authCode: string) {
    // send auth code to server in exchange for cookie
    this.http
      .post<any>(`${this.env.apiUrlRoot}/User/Authenticate`, {
        authCode,
        redirectUri: this.authCodeFlowConfig.redirectUri
      })
      .subscribe(resp => {
        this.userLoginStatus.next(true);
        console.log(resp);
        this._getUser().subscribe(
          (u: User) => {
            this._user = u;
            sessionStorage.setItem('apollo-user', JSON.stringify(this._user));
            this.store.dispatch(new AuthenticationLogin(u, authCode)).subscribe();

            // let anyone listening know that we are done logging in
            this.isLoginComplete$.next(true);
            this.loadingScreenService.stopLoading();
            this._auditSessionEvent('user-logged-in');

            // get redirect url from session storage & remove key
            let redirect = sessionStorage.getItem('auth-redirect') || '/';
            sessionStorage.removeItem('auth-redirect');

            this.router.navigate([redirect]);
            this.checkBrowserCompatibility();
          },
          err => {
            console.log(err);
            if (err.status == 403) {
              //If we're getting a 401 here
              //That means we're failing the server-side
              //role checks. Send to Access Denied page.
              this.router.navigate(['/access-denied']);
            }
          }
        );
      });
  }

  public hasPermission(permission: string, access: string): boolean {
    const portalPermissionRoles = this.env.permissionRoles;
    if (portalPermissionRoles == null || portalPermissionRoles.length == 0) return false;

    for (let i = 0; i < portalPermissionRoles.length; i++) {
      const rolePermissions = this._user.Permissions[portalPermissionRoles[i]];
      if (rolePermissions == null || rolePermissions[permission] == null) continue;
      if (rolePermissions[permission] >= access) return true;
    }
    return false;

    //  if (this._user && this._user.Permissions && this._user.Permissions[permission]) {
    //    // if a user has W permission, that implies R
    //    if (access == AccessLevels.Read)
    //      return (
    //        this._user.Permissions[permission].indexOf(access) > -1 ||
    //        this._user.Permissions[permission].indexOf(AccessLevels.Write) > -1
    //      );
    //    else return this._user.Permissions[permission].indexOf(access) > -1;
    //  }

    //  return false;
  }

  // if the logged-in user is a member of any of the passed-in roles, return true
  public isInAnyRole(roles: string[]): boolean {
    if (!this._user || !this._user.Roles) {
      return false;
    }

    roles = roles || [];
    return _.some(roles, (r: any) => _.contains(this._user.Roles, r));
  }

  public isInAllowedRole(): boolean {
    const allowedRoles = this.env.allowedRoles;
    return this.isInAnyRole(allowedRoles);
  }

  // task 8018: this function returns a boolean based on
  // the presence of an 'apollo-user' session storage key.
  // if the user opens a new tab request, check for an
  // active session and attempt to refetch & initialize the user
  public isLoggedIn(): Observable<boolean> {
    if (this.user != null && sessionStorage.getItem('apollo-user')) {
      // user data is set
      this.userLoginStatus.next(true);
      return of(true);
    }

    // user data is not set and auth is not processing,
    // attempt to refetch user data.
    return this._getUser().pipe(
      switchMap((u: User) => {
        // user record found, reinitialize data
        this._user = u;
        sessionStorage.setItem('apollo-user', JSON.stringify(this._user));
        this.userLoginStatus.next(true);
        return of(true);
      }),
      catchError(err => {
        return of(false);
      })
    );
  }

  public isCurrentUserFirstTen(id: string): boolean {
    const legacyId = this.getCurrentLegacyUserId();
    const email = this.getCurrentEmail().substring(0, 10);
    if (legacyId != null && id === legacyId) {
      return true;
    }
    if (email != null && id === email) {
      return true;
    }
    return false;
  }

  public isCurrentUser(id: string): boolean {
    const legacyId = this.getCurrentLegacyUserId();
    const email = this.getCurrentEmail();
    if (legacyId != null && id === legacyId) {
      return true;
    }
    if (email != null && id.toLowerCase() === email.toLowerCase()) {
      return true;
    }
    return false;
  }

  public getCurrentEmail(): string | null {
    if (this.user) {
      return this.user.Email;
    }
    return null;
  }

  public getCurrentLegacyUserId(): string | null {
    if (this.user) {
      return this.user.LegacyUserId;
    }
    return null;
  }

  public getCurrentUserName(): string | null {
    if (this.user) {
      return this.user.Username;
    }
    return null;
  }

  public getCurrentUserId(): string | null {
    if (this.user) {
      return this.user.UserId;
    }
    return null;
  }

  public startAuthentication() {
    if (this.env.redirectToExternalLogin) {
      window.location.href = this.env.redirectToExternalLogin;
    } else {
      this.oauthService.configure(this.authCodeFlowConfig);

      this.oauthService.loadDiscoveryDocument().then(() => {
        return this.oauthService.initCodeFlow();
      });
    }
  }

  public signOut(authenticationPassthroughRoute: ActivatedRoute = null) {
    // run pre-sign out clean up process
    // use finalize incase any requests fail inside,
    // the user is still able to signout out
    this.signOutTools
      .preSignOutCleanUp(this.getCurrentUserId())
      .pipe(
        finalize(() => {
          sessionStorage.removeItem('apollo-user');
          this._user = null;

          const headers = new HttpHeaders().set(this.loadingInterceptorSkipHeader, '');

          this.http
            .post<any>(`${this.env.apiUrlRoot}/User/LogOut`, {}, { headers })
            .subscribe(() => {
              this.oauthService.configure(this.authCodeFlowConfig);
              this.oauthService.logOut(true);

              this.ngModalService.dismissAll();
              this.userLoginStatus.next(false);
              if (authenticationPassthroughRoute != null) {
                this.completeAuthentication(
                  authenticationPassthroughRoute.snapshot.queryParams.code
                );
              } else {
                this.ngZone.run(() => {
                  this.router.navigate(['/logged-out']);
                });
              }
            });
        })
      )
      .subscribe();
  }

  public forceLoggedOutState() {
    this.store.dispatch(new ClearClientState());
    this.store.dispatch(new ClearAuthState());
    this.userLoginStatus.next(false);
  }

  public doesAuthRequestTokenMatch(token: string) {
    return this.store.select(AuthenticationState.authCode).pipe(
      map(stored => {
        return stored == token;
      })
    );
  }

  // #region private methods

  private _auditSessionEvent(eventName: string) {
    this.isLoggedIn().subscribe(resp => {
      if (resp) {
        this.auditService.audit(eventName, {
          User: this.getCurrentUserName()
        });
      }
    });
  }

  private _getUser(): Observable<User> {
    const headers = new HttpHeaders()
      .set(this.errorInterceptorSkipHeader, '')
      .set(this.loginRedirectSkipHeader, '');
    return this.http.get<User>(`${this.env.apiUrlRoot}/User`, { headers });
  }

  private _setUser() {
    if (!this._user) {
      const user = sessionStorage.getItem('apollo-user');

      const parsed = JSON.parse(user ?? '{}');

      if (user && parsed) {
        this._user = parsed;
      }
    }
  }

  // #endregion
}
